import { create } from 'zustand';
import type { ExerciseEvent } from '../types/domain';

// Live command-center state fed by the Round-2 socket events. Query panels still
// fetch their own snapshots; this store carries the *live deltas* that need to
// drive animations/alerts immediately (battle overlay, feed ticker, who's-dark).

export interface ActiveExecution {
  targetId: string;
  operationType: 'RAID' | 'AMBUSH' | 'CACHE_HUNT';
  platoonId?: string | null;
  callsign?: string | null;
  startedAt: string;
}

const FEED_CAP = 300;

interface CcState {
  // targetId -> live execution (drives the animated battle overlay on the map)
  activeExecutions: Record<string, ActiveExecution>;
  startExecution: (e: ActiveExecution) => void;
  stopExecution: (targetId: string) => void;

  // rolling war-diary buffer for the live feed + KPI ticker
  recentFeed: ExerciseEvent[];
  pushFeed: (ev: ExerciseEvent) => void;
  setFeed: (evs: ExerciseEvent[]) => void;

  // platoonId -> dark (comms lost > 5 min)
  commsDark: Record<string, boolean>;
  setCommsDark: (platoonId: string, dark: boolean) => void;

  // last proximity ping (targetId+platoonId) for one-shot alerting
  lastProximity: { targetId: string; platoonId: string; at: string } | null;
  setProximity: (p: { targetId: string; platoonId: string; at: string }) => void;
}

export const useCcStore = create<CcState>((set) => ({
  activeExecutions: {},
  startExecution: (e) =>
    set((s) => ({ activeExecutions: { ...s.activeExecutions, [e.targetId]: e } })),
  stopExecution: (targetId) =>
    set((s) => {
      if (!s.activeExecutions[targetId]) return s;
      const next = { ...s.activeExecutions };
      delete next[targetId];
      return { activeExecutions: next };
    }),

  recentFeed: [],
  pushFeed: (ev) =>
    set((s) => {
      if (!ev || !ev.id) return s;
      if (s.recentFeed.some((e) => e.id === ev.id)) return s;
      return { recentFeed: [ev, ...s.recentFeed].slice(0, FEED_CAP) };
    }),
  setFeed: (evs) => set({ recentFeed: (evs || []).slice(0, FEED_CAP) }),

  commsDark: {},
  setCommsDark: (platoonId, dark) =>
    set((s) => ({ commsDark: { ...s.commsDark, [platoonId]: dark } })),

  lastProximity: null,
  setProximity: (p) => set({ lastProximity: p }),
}));
